import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";

import { useLocalization } from "@shared-ui/localization-context";

import { FlightHiddenSearchDataProps, WizardFlightPWAViewProps } from "../typings";
import { RoundtripFlight } from "../components/RoundtripFlight";
import { OnewayFlight } from "../components/OnewayFlight";
import { MulticityFlight } from "../components/MulticityFlight";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { FlightHiddenSearchData } from "../components/FlightHiddenSearchData";
import { WizardFlightPWA } from "../WizardFlightPWA";
import { FHCHiddenSearchData } from "../components/FHCHiddenSearchData";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { locKeys } from "../components/l10n";
import { FlightType } from "src/stores/wizard/state/typings";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { liveAnnounce, Level } from "uitk-react-utils";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { PreferredClassInput } from "components/shared/PreferredClassInput/PreferredClassInput";
import { PreferredAirlineInput } from "components/shared/PreferredAirlineInput/PreferredAirlineInput";
import { TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "@shared-ui/viewport-context";
import { UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { StickyRoundtripFlight } from "../components/StickyRoundTripFlight";
import { StickyOnewayFlight } from "../components/StickyOnewayFlight";
import { UitkSheet } from "uitk-react-sheet";
import { UitkPrimaryButton } from "uitk-react-button";
import { UitkSpacing } from "uitk-react-spacing";
import { useRefTargetScroll, useRefContext } from "@shared-ui/ref-context";

enum ViewSize {
  SMALL = "small",
  MEDIUM = "medium",
}

const MobileCTA = ({
  view,
  ctaVisible,
  onClickScrollToWizard,
  displayText,
}: {
  view: ViewSize;
  ctaVisible: boolean;
  onClickScrollToWizard: () => void;
  displayText: string;
}) => {
  if (view !== ViewSize.SMALL) {
    return null;
  }

  return (
    <UitkSheet className="mobileStickyWizardCTA" isVisible={ctaVisible}>
      <UitkSpacing padding={{ block: "four", inline: "three" }} className="elevation-24">
        <div>
          <UitkLayoutFlex justifyContent={"space-between"} wrap="nowrap" alignItems="center" space="two">
            <>
              <UitkLayoutFlexItem>
                <UitkPrimaryButton
                  data-testid="MobileStickyCTA"
                  onClick={onClickScrollToWizard}
                  text={displayText}
                  isFullWidth={true}
                />
              </UitkLayoutFlexItem>
            </>
          </UitkLayoutFlex>
        </div>
      </UitkSpacing>
    </UitkSheet>
  );
};

export const WizardFlightPWALPView: React.FunctionComponent<WizardFlightPWAViewProps> = observer((props) => {
  const {
    flightWizardState,
    globalWizardState,
    flightWizardState: { config: flightConfig },
  } = props.wizardState;
  const { formatText } = useLocalization();
  const { registerTarget } = useRefContext();
  const { scrollTo } = useRefTargetScroll();
  const [isSticky, setSticky] = React.useState(false);
  const [isCTASticky, setCTASticky] = React.useState(false);
  const stickyWizardRef = React.useRef<HTMLDivElement>(null);
  const mobileWizardWrapper = registerTarget<HTMLDivElement>("WizardWrapperSticky");
  const tabIdPrefix = "wizard-flight-tab-";
  const { flightClassCode, listOfAdditionalLegs, subLOBState } = flightWizardState;
  const currentTab = subLOBState; // tab state is calculated based on subLOB

  flightWizardState.overrideConfig(() => {
    flightWizardState.config.enableNonStopFlight = true;
  });

  if (listOfAdditionalLegs.length > 1) {
    liveAnnounce(formatText(locKeys.multiCityFlightLegLabel, listOfAdditionalLegs.length + 1), Level.POLITE);
  }

  const onClickScrollToWizard = (): void => {
    scrollTo("WizardWrapperSticky", []);
  };

  const flightHiddenSearchDataProps: FlightHiddenSearchDataProps = {
    flightType: "on",
    flightClass: WizardFlightPWA.getFlightClassCodeForURL(flightClassCode),
    mode: "search",
    trip: subLOBState,
    leg1: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[0],
    leg2: WizardFlightPWA.getLegURLFragment(flightWizardState, flightConfig)[1],
    flightAirline: globalWizardState.flightAirline,
    flightWizardState: props.wizardState.flightWizardState,
    maxhops: props.wizardState.flightWizardState.isNonStopFlightChecked ? "maxhops:0" : "",
    globalWizardState,
  };

  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 2, large: 2 };
  const preferredClassColSpan: UitkLayoutConditionalGridSpan = { small: 3, medium: 2, large: 2 };
  const { config, isAddAHotelChecked, isAddACarChecked } = flightWizardState;
  const travelerConfig =
    config.enableAddHotel && isAddAHotelChecked
      ? config.travelersWithHotel
      : config.enableAddCar && isAddACarChecked
      ? config.travelersWithCar
      : config.travelers;

  /**
   * Value used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = flightConfig.travelers;

  /**
   * Set the new tab to display and update global state
   */
  const handleTabChange = (flightType: FlightType) => {
    props.onSelectFlightType(flightType);
  };

  const handleScroll = () => {
    if (stickyWizardRef && stickyWizardRef.current) {
      const stickyPosition = stickyWizardRef.current.getBoundingClientRect().bottom < 0;
      const packageAddition = !(flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked);
      const notMulticityLob = flightWizardState.subLOBState !== FlightType.MULTI_CITY;
      const shouldSetSticky = stickyPosition && packageAddition && notMulticityLob;
      setSticky(shouldSetSticky);
    } else if (mobileWizardWrapper && mobileWizardWrapper.current) {
      const ctaPosition = mobileWizardWrapper.current.getBoundingClientRect().bottom < 0;
      setCTASticky(ctaPosition);
    } else {
      setSticky(false);
      setCTASticky(false);
    }
  };
  React.useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, []);
  /**
   * Builds the Travelers and PreferredClassInput
   * as a prop to be sent to UitkExperimentalButtonTabs
   * to display them next to the UitkTabs, at the right
   */
  const noNavigationItems = () => (
    <Viewport>
      <ViewLarge>
        <UitkLayoutFlex alignItems="center" space="six" className="all-x-padding-six">
          <UitkLayoutFlexItem>
            <Travelers
              lobState={flightWizardState}
              config={travelerConfig}
              colSpan={travelersColSpan}
              noRehydratationValues={noRehydratationValues}
              triggerType={TravelersTriggerTypes.LINK_BUTTON}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredClassInput
              colSpan={preferredClassColSpan}
              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <PreferredAirlineInput
              onSelectPreferredAirline={props.onSelectPreferredAirline}
              wizardState={props.wizardState}
            />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </ViewLarge>
    </Viewport>
  );

  const referrerId =
    currentTab !== FlightType.MULTI_CITY ? flightConfig.referrerId : flightConfig.multiCityFlight.referrerId;

  const { pageHeading, pageSubHeadline } = props.compositionStore.composition!;

  const titleFromComposition = (props.flexComponent.model.titleFromPageHeader && pageHeading) || undefined;
  const subTitleFromComposition = (props.flexComponent.model.subTitleFromPageSubHeader && pageSubHeadline) || undefined;
  const showWhenNotSticky = !isSticky;
  const wizardFormTestId = isSticky ? "WizardFlightPWALP-sticky" : "WizardFlightPWALP";

  const renderContent = (view: ViewSize, ref: React.RefObject<HTMLDivElement>) => {
    return (
      <div className="WizardFlightPWALP-Wrapper" id="WizardWrapperSticky" ref={ref}>
        <form
          noValidate={true}
          className={`WizardFlightPWALP ${isSticky ? "sticky all-b-padding-three SimpleContainer" : ""}`}
          action={
            flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked
              ? "/flexibleshopping"
              : "/Flights-Search"
          }
          onSubmit={flightWizardState.submit}
          id={flightConfig.elementId}
          data-fm={props.flexComponent.fmId}
          data-fm-title-id={props.flexComponent.fmTitleId}
          data-testid={wizardFormTestId}
        >
          {flightWizardState.isAddAHotelChecked || flightWizardState.isAddACarChecked ? (
            <FHCHiddenSearchData flightWizardState={props.wizardState.flightWizardState} />
          ) : (
            <FlightHiddenSearchData {...flightHiddenSearchDataProps} />
          )}

          {showWhenNotSticky && (
            <>
              <LobTitle lobState={flightWizardState} customTitle={titleFromComposition} />
              <LobSubtitle lobState={flightWizardState} customSubTitle={subTitleFromComposition} />
            </>
          )}
          {/* Each tab represents a flights sub LOB (roundtrip, one way and multicity) */}
          <UitkExperimentalButtonTabs
            className="all-t-margin-three flightTypeTabs"
            selectedTab={currentTab}
            onTabSelect={handleTabChange}
            tabsType={UitkTabsType.NATURAL}
            tabAnchorType={UitkTabAnchorType.BUTTON}
            noNavigationItems={noNavigationItems}
            data-testid={currentTab}
          >
            <UitkTab
              name={FlightType.ROUND_TRIP}
              displayName={formatText(locKeys.flightRoundTripText)}
              targetURI={`?flightType=${FlightType.ROUND_TRIP}`}
              id={`${tabIdPrefix}${FlightType.ROUND_TRIP}`}
            >
              {currentTab === FlightType.ROUND_TRIP && (
                <>
                  {isSticky ? (
                    <StickyRoundtripFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
                  ) : (
                    <RoundtripFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
                  )}
                </>
              )}
            </UitkTab>

            <UitkTab
              name={FlightType.ONE_WAY}
              displayName={formatText(locKeys.flightOneWayText)}
              targetURI={`?flightType=${FlightType.ONE_WAY}`}
              id={`${tabIdPrefix}${FlightType.ONE_WAY}`}
            >
              {currentTab === FlightType.ONE_WAY && (
                <>
                  {isSticky ? (
                    <StickyOnewayFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
                  ) : (
                    <OnewayFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
                  )}
                </>
              )}
            </UitkTab>

            <UitkTab
              name={FlightType.MULTI_CITY}
              displayName={formatText(locKeys.flightMultiCityText)}
              targetURI={`?flightType=${FlightType.MULTI_CITY}`}
              id={`${tabIdPrefix}${FlightType.MULTI_CITY}`}
              className={`${isSticky ? "is-hidden" : ""}`}
            >
              {currentTab === FlightType.MULTI_CITY && (
                <MulticityFlight lobState={flightWizardState} flightConfig={flightConfig} {...props} />
              )}
            </UitkTab>
          </UitkExperimentalButtonTabs>
          <Viewport>
            <ViewSmall>
              <UitkLayoutFlex alignItems="center" space="six" className="all-t-padding-four">
                <UitkLayoutFlexItem>
                  <div>
                    <Travelers
                      lobState={flightWizardState}
                      config={travelerConfig}
                      colSpan={travelersColSpan}
                      noRehydratationValues={noRehydratationValues}
                      triggerType={TravelersTriggerTypes.LINK_BUTTON}
                    />
                  </div>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <PreferredClassInput
                    colSpan={preferredClassColSpan}
                    onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                    wizardState={props.wizardState}
                  />
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <PreferredAirlineInput
                    onSelectPreferredAirline={props.onSelectPreferredAirline}
                    wizardState={props.wizardState}
                  />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </ViewSmall>
            <ViewLarge />
          </Viewport>
          {showWhenNotSticky && (
            <SubmitRow lobState={flightWizardState} rfrr={referrerId} globalConfig={globalWizardState.config} />
          )}
        </form>
        <MobileCTA
          view={view}
          ctaVisible={isCTASticky}
          onClickScrollToWizard={onClickScrollToWizard}
          displayText={formatText("wizard.mobile.label.flight")}
        />
      </div>
    );
  };

  return (
    <Viewport>
      <ViewSmall>{renderContent(ViewSize.SMALL, mobileWizardWrapper)}</ViewSmall>
      <ViewMedium>{renderContent(ViewSize.MEDIUM, stickyWizardRef)}</ViewMedium>
    </Viewport>
  );
});
